import { useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { FOLLOW_UP_KINDS, FOLLOW_UP_KIND_LABELS, formatCents, type CrmOverview, type CrmQueueOffer, type FollowUp, type FollowUpKind, type Paginated } from '@kontor/shared';
import { api, ApiError, queryString } from '../../api';
import { Bar, ColumnChart, Donut, Legend, type ColumnSeries, type Slice } from '../../components/charts';
import { Modal } from '../../components/Modal';
import { useSession } from '../../session';
import { FollowUpActions, FollowUpLine, addDays, dayOf, useFollowUpRefresh } from './FollowUps';
import './crm.css';

const errorText = (e: unknown, fallback: string) => (e instanceof ApiError ? (e.status === 422 ? (Object.values(e.fields)[0] ?? e.message) : e.message) : fallback);
const percent = (part: number, whole: number) => (whole > 0 ? `${Math.round((part / whole) * 100)} %` : '–');
const MONTHS = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];
/** So viele Einträge zeigt eine Karte, der Rest steht in der ganzen Liste im Fenster */
const SHOWN = 3;

/** Symbole: Linien auf einem 24er-Raster, die Farbe kommt aus dem Text */
const ICONS = {
  inbox: 'M4 13l2-8h12l2 8M4 13v6h16v-6M4 13h5l1 2h4l1-2h5',
  doc: 'M7 3h7l5 5v13H7zM14 3v5h5M10 13h6M10 17h6',
  clock: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM12 7v5l3 2',
  check: 'M5 12l5 5 9-10',
  users: 'M9 11a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM3 20c0-3.3 2.7-6 6-6s6 2.7 6 6M17 11a2.5 2.5 0 1 0 0-5M17 14c2.5 0 4 2 4 5',
  mail: 'M4 6h16v12H4zM4 7l8 6 8-6',
  expand: 'M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7',
  right: 'M5 12h14M13 6l6 6-6 6',
} as const;

function Icon({ name, size = 18 }: { name: keyof typeof ICONS; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d={ICONS[name]} />
    </svg>
  );
}

function Tile({ label, value, sub, to, tone, icon, accent = 'blue' }: { label: string; value: string; sub?: string; to?: string; tone?: 'warn' | 'bad'; icon: keyof typeof ICONS; accent?: 'blue' | 'green' | 'red' | 'amber' }) {
  const body = (
    <>
      <span className="crm-tile__head"><span className={`crm-icon crm-icon--${accent}`}><Icon name={icon} size={16} /></span><span className="crm-tile__label">{label}</span></span>
      <strong className={tone ? `crm-${tone}` : ''}>{value}</strong>
      {sub && <span className="muted small">{sub}</span>}
    </>
  );
  return to ? <Link to={to} className="card crm-tile crm-tile--link">{body}</Link> : <div className="card crm-tile">{body}</div>;
}

/** Kopf einer Karte: Überschrift links, rechts eine Schaltfläche in der Ecke, die die ganze Liste öffnet */
function CardHead({ id, title, hint, count, shown, onAll, to }: { id: string; title: string; hint?: string; count?: number; shown?: number; onAll?: () => void; to?: string }) {
  const more = count !== undefined && shown !== undefined && count > shown;
  return (
    <div className="crm-card-head">
      <div>
        <h2 id={id}>{title}</h2>
        {hint && <p className="muted small">{hint}</p>}
      </div>
      {more && onAll && (
        <button type="button" className="btn btn--secondary btn--sm crm-all" onClick={onAll} aria-label={`${title}: alle ${count} anzeigen`}><Icon name="expand" size={14} /> Alle {count}</button>
      )}
      {more && to && <Link to={to} className="btn btn--secondary btn--sm crm-all">Alle {count} <Icon name="right" size={14} /></Link>}
    </div>
  );
}

/** Eine Zeile „Angebot … wartet seit N Tagen“ mit dem Schalter, in drei Tagen nachzufassen */
function OfferRow({ offer, kind, onFollow, pending }: { offer: CrmQueueOffer; kind: 'waiting' | 'expiring'; onFollow?: () => void; pending?: boolean }) {
  return (
    <li className="crm-row">
      <div>
        <Link to={`/angebote/${offer.id}`} className="strong-link">{offer.number ?? 'Entwurf'}</Link> · {offer.subject}
        <div className="muted small">{offer.contactName} · {formatCents(offer.netCents)} netto · {kind === 'waiting' ? `seit ${offer.daysOpen} Tagen ohne Antwort` : `gültig bis ${offer.validUntil ? dayOf(offer.validUntil) : '–'}`}</div>
      </div>
      {onFollow && <button type="button" className="btn btn--secondary btn--sm" disabled={pending} onClick={onFollow}>In 3 Tagen nachfassen</button>}
    </li>
  );
}

/** Trichter des Jahres: je Stufe ein Balken im Verhältnis zur größten Zahl, dahinter die Quote zur Bezugsgröße */
function Funnel({ f }: { f: NonNullable<CrmOverview['funnel']> }) {
  const rows = [
    { label: 'Anfragen', value: f.inquiries, base: f.inquiries, of: '', color: 1 },
    { label: 'Angebote versendet', value: f.offersSent, base: f.inquiries, of: 'der Anfragen', color: 1 },
    { label: 'Angebote angenommen', value: f.offersAccepted, base: f.offersSent, of: 'der Angebote', color: 2 },
    { label: 'Aufträge', value: f.orders, base: f.inquiries, of: 'der Anfragen', color: 2 },
  ];
  const max = Math.max(1, ...rows.map((r) => r.value));
  return (
    <ul className="crm-funnel">
      {rows.map((r, i) => (
        <li key={r.label}>
          <div className="crm-funnel__head">
            <span>{r.label}</span>
            <span><strong>{r.value}</strong>{i > 0 && <span className="crm-chip">{percent(r.value, r.base)} {r.of}</span>}</span>
          </div>
          <Bar value={r.value} max={max} color={r.color} />
        </li>
      ))}
    </ul>
  );
}

/** Farbe und Name der Stände im Kreisdiagramm der Angebote */
const OFFER_SLICES: Record<string, { label: string; color: number }> = {
  sent: { label: 'Offen', color: 1 },
  accepted: { label: 'Angenommen', color: 2 },
  rejected: { label: 'Abgelehnt', color: 7 },
  expired: { label: 'Abgelaufen', color: 8 },
};

function OfferStatusCard({ o }: { o: NonNullable<CrmOverview['offers']> }) {
  const [active, setActive] = useState<string | null>(null);
  const slices: Slice[] = Object.entries(OFFER_SLICES).map(([key, def]) => ({ key, label: def.label, color: def.color, value: o.yearByStatus.find((s) => s.status === key)?.count ?? 0 })).filter((s) => s.value > 0);
  const total = slices.reduce((s, x) => s + x.value, 0);
  const won = o.yearByStatus.find((s) => s.status === 'accepted');
  return (
    <section className="card" aria-labelledby="crm-status">
      <CardHead id="crm-status" title="Angebote nach Stand" hint="Im laufenden Jahr angelegt, ohne Entwürfe." />
      {total === 0 ? (
        <p className="muted small">Noch keine versendeten Angebote in diesem Jahr.</p>
      ) : (
        <>
          <div className="crm-status">
            <Donut slices={slices} active={active} onActive={setActive} title="Angebote" total={String(total)} format={(v) => String(v)} ariaLabel={`Angebote nach Stand, zusammen ${total}`} />
            <Legend slices={slices} active={active} onActive={setActive} format={(v) => `${v}`} />
          </div>
          {won && won.count > 0 && <p className="muted small crm-status__note">Angenommen: {won.count} mit {formatCents(won.netCents)} netto. Rechnerische Annahmequote {percent(won.count, total)}, die noch offenen Angebote zählen dabei mit.</p>}
        </>
      )}
    </section>
  );
}

/**
 * CRM-Abteilung (KON-172): der Arbeitsplatz des Vertriebs. Oben die Kennzahlen, links, was heute zu tun ist (fällige Wiedervorlagen, neue Anfragen, wartende Angebote), rechts der Überblick
 * mit Trichter und Kreisdiagramm, darunter der Verlauf des Jahres und die nächsten Wiedervorlagen. Karten zeigen die ersten Einträge, die Schaltfläche in der Ecke öffnet die ganze Liste in einem Fenster.
 * Jeder Bereich erscheint nur, wenn die Rolle das Modul auch sonst sehen darf.
 */
export function CrmPage() {
  const { can, hasModule } = useSession();
  const refresh = useFollowUpRefresh();
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<null | 'due' | 'open' | 'inquiries' | 'waiting' | 'expiring'>(null);
  const overview = useQuery({ queryKey: ['crm-overview'], queryFn: async () => (await api<{ data: CrmOverview }>('GET', '/crm/overview')).data, refetchInterval: 60_000, refetchOnWindowFocus: true });
  const next = useQuery({
    queryKey: ['follow-ups', 'page', 'open', 'preview'],
    queryFn: async () => (await api<Paginated<FollowUp>>('GET', `/crm/follow-ups${queryString({ scope: 'open', perPage: 5 })}`)).data,
    enabled: !!overview.data?.followUps,
  });
  const follow = useMutation({
    mutationFn: (offer: CrmQueueOffer) => api('POST', '/crm/follow-ups', { subjectKind: 'offer', subjectId: offer.id, dueOn: addDays(3), note: 'Nachfassen' }),
    onSuccess: () => { refresh(); setError(null); },
    onError: (e) => setError(errorText(e, 'Die Wiedervorlage konnte nicht angelegt werden.')),
  });
  const o = overview.data;
  const write = can('crm.write');

  const series: ColumnSeries[] = [];
  if (o?.trend?.inquiries) series.push({ key: 'inquiries', label: 'Anfragen', color: 1, values: o.trend.inquiries });
  if (o?.trend?.offersSent) series.push({ key: 'sent', label: 'Angebote versendet', color: 4, values: o.trend.offersSent });
  if (o?.trend?.offersAccepted) series.push({ key: 'accepted', label: 'Angebote angenommen', color: 2, values: o.trend.offersAccepted, off: true });
  if (o?.trend?.orders) series.push({ key: 'orders', label: 'Aufträge', color: 3, values: o.trend.orders });
  if (o?.trend?.newContacts) series.push({ key: 'contacts', label: 'Neue Kontakte', color: 6, values: o.trend.newContacts, off: true });
  const hasTrend = series.some((s) => s.values.some((v) => v > 0));

  return (
    <div className="crm-page">
      <div className="page-head">
        <h1>CRM Dashboard</h1>
        <span className="muted">Anfragen, Angebote und Kontakte im Blick</span>
        <span className="page-head__actions crm-quick">
          {hasModule('inquiries') && can('inquiries.write') && <Link to="/anfragen/neu" className="btn btn--secondary btn--sm">Neue Anfrage</Link>}
          {hasModule('offers') && can('offers.write') && <Link to="/angebote/neu" className="btn btn--secondary btn--sm">Neues Angebot</Link>}
          {hasModule('contacts') && can('contacts.write') && <Link to="/kontakte/neu" className="btn btn--secondary btn--sm">Neuer Kontakt</Link>}
        </span>
      </div>
      {overview.isError && <div className="alert alert--error" role="alert">{(overview.error as Error).message}</div>}
      {error && <div className="alert alert--error" role="alert">{error}</div>}
      {overview.isLoading && <p className="muted">Wird geladen …</p>}
      {o && (
        <>
          <div className="crm-tiles">
            {o.inquiries && <Tile icon="inbox" accent={o.inquiries.oldestNewDays !== null && o.inquiries.oldestNewDays >= 3 ? 'amber' : 'blue'} label="Neue Anfragen" value={String(o.inquiries.new)} to="/anfragen" tone={o.inquiries.oldestNewDays !== null && o.inquiries.oldestNewDays >= 3 ? 'warn' : undefined} sub={o.inquiries.new > 0 && o.inquiries.oldestNewDays !== null ? `älteste wartet ${o.inquiries.oldestNewDays} Tage · ${o.inquiries.inProgress} in Bearbeitung` : `${o.inquiries.inProgress} in Bearbeitung`} />}
            {o.offers && <Tile icon="doc" label="Offene Angebote" value={String(o.offers.open)} to="/angebote" sub={`${formatCents(o.offers.openNetCents)} netto · ${o.offers.drafts} Entwürfe`} />}
            {o.followUps && <Tile icon="clock" accent={o.followUps.due > 0 ? 'red' : 'green'} label="Wiedervorlagen fällig" value={String(o.followUps.due)} tone={o.followUps.due > 0 ? 'bad' : undefined} sub={`${o.followUps.open} offen insgesamt`} />}
            {o.offers && <Tile icon="check" accent="green" label="Angenommen diesen Monat" value={String(o.offers.acceptedThisMonth)} sub={`${formatCents(o.offers.acceptedNetCentsThisMonth)} netto`} />}
            {o.contacts && <Tile icon="users" label="Kontakte" value={o.contacts.total.toLocaleString('de-DE')} to="/kontakte" sub={`${o.contacts.newThisMonth} neu diesen Monat`} />}
            {o.marketing && <Tile icon="mail" accent="amber" label="Newsletter" value={o.marketing.subscribed.toLocaleString('de-DE')} to="/marketing" sub={`Abonnenten · ${o.marketing.campaignsSent} Kampagnen versendet`} />}
          </div>

          <div className="crm-cols">
            <div className="crm-col">
              <h2 className="crm-col__title">Heute zu tun</h2>
              {o.followUps && (
                <section className="card" aria-labelledby="crm-due">
                  <CardHead id="crm-due" title="Heute nachfassen" count={o.followUps.due} shown={SHOWN} onAll={() => setOpen('due')} />
                  {o.followUps.items.length === 0 ? (
                    <p className="muted small">Keine fällige Wiedervorlage. Gut gemacht.</p>
                  ) : (
                    <ul className="crm-list">
                      {o.followUps.items.slice(0, SHOWN).map((f) => (
                        <li key={f.id} className="fu-item">
                          <FollowUpLine item={f} />
                          {write && <FollowUpActions item={f} />}
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
              )}

              {o.inquiries && (
                <section className="card" aria-labelledby="crm-inq">
                  <CardHead id="crm-inq" title="Neue Anfragen" count={o.inquiries.new} shown={Math.min(SHOWN, o.inquiries.recent.length)} to="/anfragen?status=new" />
                  {o.inquiries.recent.length === 0 ? <p className="muted small">Keine neue Anfrage.</p> : (
                    <ul className="crm-list">
                      {o.inquiries.recent.slice(0, SHOWN).map((i) => (
                        <li key={i.id} className="crm-row">
                          <div>
                            <Link to={`/anfragen/${i.id}`} className="strong-link">{i.reference}</Link> · {i.name}
                            <div className="muted small">{i.type} · eingegangen {dayOf(i.createdAt.slice(0, 10))}</div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
              )}

              {o.offers && (
                <section className="card" aria-labelledby="crm-offers">
                  <CardHead id="crm-offers" title="Angebote warten auf Antwort" hint="Seit mindestens 7 Tagen ohne Antwort und ohne Wiedervorlage." count={o.offers.awaitingReply.length} shown={SHOWN} onAll={() => setOpen('waiting')} />
                  {o.offers.awaitingReply.length === 0 ? <p className="muted small">Kein Angebot hängt.</p> : (
                    <ul className="crm-list">{o.offers.awaitingReply.slice(0, SHOWN).map((x) => <OfferRow key={x.id} offer={x} kind="waiting" pending={follow.isPending} onFollow={write ? () => follow.mutate(x) : undefined} />)}</ul>
                  )}
                </section>
              )}
              {o.offers && (
                <section className="card" aria-labelledby="crm-expiring">
                  <CardHead id="crm-expiring" title="Angebote laufen bald ab" hint="Gültig bis in den nächsten 7 Tagen." count={o.offers.expiringSoon.length} shown={SHOWN} onAll={() => setOpen('expiring')} />
                  {o.offers.expiringSoon.length === 0 ? <p className="muted small">Keines läuft in den nächsten Tagen ab.</p> : <ul className="crm-list">{o.offers.expiringSoon.slice(0, SHOWN).map((x) => <OfferRow key={x.id} offer={x} kind="expiring" />)}</ul>}
                </section>
              )}
            </div>

            <div className="crm-col">
              <h2 className="crm-col__title">Im Blick</h2>
              {o.funnel && (
                <section className="card" aria-labelledby="crm-funnel">
                  <CardHead id="crm-funnel" title={`Trichter ${o.funnel.year}`} hint="Was in diesem Jahr angelegt wurde und wie viel davon zu einem Auftrag führte." />
                  <Funnel f={o.funnel} />
                  <p className="small"><Link to="/auftragscockpit">Zur Auftragsübersicht</Link></p>
                </section>
              )}
              {o.offers && o.funnel && <OfferStatusCard o={o.offers} />}
            </div>
          </div>

          {hasTrend && o.trend && (
            <section className="card crm-trend" aria-labelledby="crm-trend">
              <CardHead id="crm-trend" title={`Verlauf ${o.trend.year}`} hint="Was je Monat angelegt wurde. Die Einträge oben blenden Reihen ein und aus." />
              <ColumnChart labels={MONTHS} series={series} ariaLabel={`Anfragen, Angebote und Aufträge je Monat ${o.trend.year}`} />
            </section>
          )}

          {o.followUps && (
            <section className="card" aria-labelledby="crm-all">
              <CardHead id="crm-all" title="Nächste Wiedervorlagen" hint="Die fünf nächsten offenen, mit Filtern in der ganzen Liste." count={o.followUps.open} shown={5} onAll={() => setOpen('open')} />
              {next.data?.length === 0 && <p className="muted small">Keine offenen Wiedervorlagen. Sie legen sie an Anfragen, Angeboten, Aufträgen und Kontakten an („Wiedervorlage anlegen“) oder gleich hier bei einem wartenden Angebot.</p>}
              <ul className="crm-list">
                {next.data?.map((f) => (
                  <li key={f.id} className="fu-item">
                    <FollowUpLine item={f} />
                    {write && !f.doneAt && <FollowUpActions item={f} />}
                  </li>
                ))}
              </ul>
            </section>
          )}

          <FollowUpsModal open={open === 'due' || open === 'open'} initial={open === 'due' ? 'due' : 'open'} write={write} onClose={() => setOpen(null)} />
          <Modal open={open === 'waiting'} title="Angebote warten auf Antwort" onClose={() => setOpen(null)}>
            <ul className="crm-list">{o.offers?.awaitingReply.map((x) => <OfferRow key={x.id} offer={x} kind="waiting" pending={follow.isPending} onFollow={write ? () => follow.mutate(x) : undefined} />)}</ul>
          </Modal>
          <Modal open={open === 'expiring'} title="Angebote laufen bald ab" onClose={() => setOpen(null)}>
            <ul className="crm-list">{o.offers?.expiringSoon.map((x) => <OfferRow key={x.id} offer={x} kind="expiring" />)}</ul>
          </Modal>
        </>
      )}
    </div>
  );
}

/** Alle Wiedervorlagen mit Filtern für Stand, Bezug und „nur meine“ */
function FollowUpsModal({ open, initial, write, onClose }: { open: boolean; initial: 'open' | 'due'; write: boolean; onClose: () => void }) {
  return (
    <Modal open={open} title="Wiedervorlagen" wide onClose={onClose}>
      <FollowUpsList key={initial} initial={initial} write={write} />
    </Modal>
  );
}

function FollowUpsList({ initial, write }: { initial: 'open' | 'due'; write: boolean }): ReactNode {
  const [scope, setScope] = useState<'open' | 'due' | 'done'>(initial);
  const [mine, setMine] = useState(false);
  const [kind, setKind] = useState('');
  const list = useQuery({
    queryKey: ['follow-ups', 'page', scope, mine, kind],
    queryFn: async () => (await api<Paginated<FollowUp>>('GET', `/crm/follow-ups${queryString({ scope, mine: mine ? '1' : undefined, subjectKind: kind || undefined, perPage: 50 })}`)).data,
    placeholderData: keepPreviousData,
  });
  return (
    <>
      <div className="crm-filter__fields">
        <label className="field"><span className="sr-only">Stand</span>
          <select className="input" value={scope} onChange={(e) => setScope(e.target.value as 'open' | 'due' | 'done')} aria-label="Stand der Wiedervorlagen">
            <option value="open">Offen</option>
            <option value="due">Fällig</option>
            <option value="done">Erledigt</option>
          </select>
        </label>
        <label className="field"><span className="sr-only">Bezug</span>
          <select className="input" value={kind} onChange={(e) => setKind(e.target.value)} aria-label="Bezug der Wiedervorlagen">
            <option value="">Alle Bezüge</option>
            {FOLLOW_UP_KINDS.map((k: FollowUpKind) => <option key={k} value={k}>{FOLLOW_UP_KIND_LABELS[k]}</option>)}
          </select>
        </label>
        <label className="check"><input type="checkbox" checked={mine} onChange={(e) => setMine(e.target.checked)} /><span>Nur meine</span></label>
      </div>
      {list.isLoading && <p className="muted small">Wird geladen …</p>}
      {list.data?.length === 0 && <p className="muted small">Keine Wiedervorlagen.</p>}
      <ul className="crm-list">
        {list.data?.map((f) => (
          <li key={f.id} className="fu-item">
            <FollowUpLine item={f} />
            {write && !f.doneAt && <FollowUpActions item={f} />}
          </li>
        ))}
      </ul>
    </>
  );
}

